/**
 * ProfileImageCropperModal Component
 *
 * Circular profile/cover image cropper. 1:1 aspect, 800x800 output;
 * encoded back into the source image's MIME (JPEG / PNG / WebP -- see
 * MIME_TO_EXT). Uses react-easy-crop for the pan/zoom UI and consumes
 * its `croppedAreaPixels` callback as the source rect for the canvas crop.
 * Mirrors `ImageCropperModal` in this folder.
 *
 * Do not reimplement pan/zoom math on the canvas side. react-easy-crop's
 * `croppedAreaPixels` is the single source of truth for the source rect;
 * recomputing scale/offset from `crop`+`zoom` will desync with the
 * on-screen render for non-square inputs.
 */

import { ArrowLeft, HelpCircle, MoreVertical } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import Cropper from "react-easy-crop"
import { toast } from "sonner"
import { UploadLinkIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { THUMBNAIL_LIMITS } from "@/constants/media"

interface ProfileImageCropperModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  imageFile: File | null
  onCropComplete: (croppedFile: File) => void
}

const MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
}

interface CropArea {
  x: number
  y: number
  width: number
  height: number
}

// Square output -- the circular display is purely a visual mask; the
// underlying file is 800x800 so downstream avatar usages can apply
// their own round-clip / resize.
const OUTPUT_SIZE = 800

export function ProfileImageCropperModal({
  open,
  onOpenChange,
  imageFile,
  onCropComplete,
}: ProfileImageCropperModalProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  // Lower bound is derived from the image's aspect ratio so a long
  // portrait/landscape can be zoomed out until the whole image is
  // visible inside the round frame. At cover-fit (zoom = 1) the longer
  // side overflows by design; minZoom < 1 buys the user the
  // pan-and-decide UX they get from any Instagram-style cropper.
  const [minZoom, setMinZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Latched on unmount + when handleClose runs so an in-flight
  // createCroppedImage that resolves after the modal has gone away
  // doesn't fire onCropComplete or toast against a closed dialog.
  const isActiveRef = useRef(true)

  useEffect(() => {
    isActiveRef.current = true
    return () => {
      isActiveRef.current = false
    }
  }, [])

  // Own the object-URL lifecycle here only -- creating it when an
  // imageFile arrives and revoking it on cleanup. A second effect that
  // also tried to revoke based on `open` would race with this one and
  // could revoke the URL still referenced by the rendered <Cropper>.
  useEffect(() => {
    if (!imageFile || !open) return
    const url = URL.createObjectURL(imageFile)
    setImageSrc(url)
    setCrop({ x: 0, y: 0 })
    setCroppedAreaPixels(null)

    // Probe the image so we can pick a minZoom that lets the user see
    // the whole image (the longer side just fits inside the crop frame
    // at the cover-fit scale). Without this, react-easy-crop's
    // objectFit="cover" always renders with the shorter side filling
    // the frame, locking the user into a pre-zoomed view for any
    // non-square input.
    const probe = new window.Image()
    probe.onload = () => {
      const w = probe.naturalWidth
      const h = probe.naturalHeight
      const ratio = w > 0 && h > 0 ? Math.min(w, h) / Math.max(w, h) : 1
      setMinZoom(ratio)
      setZoom(ratio)
    }
    probe.src = url

    return () => {
      URL.revokeObjectURL(url)
      setImageSrc(null)
    }
  }, [imageFile, open])

  const handleCropComplete = useCallback((_: CropArea, areaPixels: CropArea) => {
    setCroppedAreaPixels(areaPixels)
  }, [])

  const handleReplaceImage = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        if (imageSrc) {
          URL.revokeObjectURL(imageSrc)
        }
        const url = URL.createObjectURL(file)
        setImageSrc(url)
        setCrop({ x: 0, y: 0 })
        setCroppedAreaPixels(null)
        // Recompute minZoom for the swapped image (see the main effect
        // for the rationale). Until the probe loads we leave the
        // previous minZoom in place; users almost never notice a sub-
        // second window before zoom settles on a new minimum.
        const probe = new window.Image()
        probe.onload = () => {
          const w = probe.naturalWidth
          const h = probe.naturalHeight
          const ratio = w > 0 && h > 0 ? Math.min(w, h) / Math.max(w, h) : 1
          setMinZoom(ratio)
          setZoom(ratio)
        }
        probe.src = url
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    },
    [imageSrc],
  )

  const createCroppedImage = useCallback(
    async (area: CropArea): Promise<File> => {
      if (!imageFile || !imageSrc) {
        throw new Error("Image not ready for cropping")
      }

      const img = new Image()
      // Defensive: imageSrc today is always a same-origin blob: URL, but
      // setting crossOrigin protects toBlob from canvas-tainting if a
      // future caller ever passes a remote URL through this component.
      img.crossOrigin = "anonymous"

      return new Promise<File>((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement("canvas")
          canvas.width = OUTPUT_SIZE
          canvas.height = OUTPUT_SIZE
          const ctx = canvas.getContext("2d")
          if (!ctx) {
            reject(new Error("No 2d context"))
            return
          }

          // Source rect comes straight from react-easy-crop; destination
          // is the full square output. The library has already done all
          // the pan/zoom math, so there's nothing to recompute here.
          ctx.drawImage(
            img,
            area.x,
            area.y,
            area.width,
            area.height,
            0,
            0,
            OUTPUT_SIZE,
            OUTPUT_SIZE,
          )

          canvas.toBlob(
            blob => {
              if (!blob) {
                reject(
                  new Error(
                    "Could not encode the cropped image (canvas may be tainted or device low on memory)",
                  ),
                )
                return
              }
              const extension = MIME_TO_EXT[imageFile.type] || "jpg"
              const croppedFile = new File(
                [blob],
                imageFile.name.replace(/\.[^.]+$/, `-cropped.${extension}`),
                {
                  type: imageFile.type,
                },
              )
              resolve(croppedFile)
            },
            imageFile.type,
            0.95,
          )
        }

        img.onerror = () => reject(new Error("Failed to load image"))
        img.src = imageSrc
      })
    },
    [imageFile, imageSrc],
  )

  const handleApply = useCallback(async () => {
    if (!croppedAreaPixels) return

    setIsProcessing(true)
    try {
      const croppedFile = await createCroppedImage(croppedAreaPixels)
      if (!isActiveRef.current) return
      onCropComplete(croppedFile)
      onOpenChange(false)
    } catch (error) {
      console.error("Profile crop failed", error)
      if (isActiveRef.current) {
        toast.error("Couldn't save the cropped image. Please try again.")
      }
    } finally {
      if (isActiveRef.current) {
        setIsProcessing(false)
      }
    }
  }, [croppedAreaPixels, createCroppedImage, onCropComplete, onOpenChange])

  const handleClose = useCallback(() => {
    isActiveRef.current = false
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    onOpenChange(false)
  }, [onOpenChange])

  const onZoomChange = useCallback((nextZoom: number) => {
    setZoom(nextZoom)
  }, [])

  const onCropChange = useCallback((nextCrop: { x: number; y: number }) => {
    setCrop(nextCrop)
  }, [])

  if (!open || !imageFile || !imageSrc) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="h-full max-w-full md:h-fit md:max-h-[90vh] md:rounded-tr-2xl md:rounded-bl-2xl bg-[#020202] md:bg-[#1C1C1E] border-0 md:border overflow-y-auto p-5 md:p-6 rounded-none md:rounded-2xl"
        onInteractOutside={e => e.preventDefault()}
      >
        <div className="flex flex-col gap-2.5">
          <div className="md:hidden sticky top-0 left-0 right-0 z-10 bg-[#020202]">
            <div className="flex justify-between items-center">
              <button type="button" onClick={handleClose} className="p-2 -ml-2 cursor-pointer">
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="p-2 -mr-2 cursor-pointer">
                    <MoreVertical className="w-5 h-5 text-white" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[160px]">
                  <DropdownMenuItem
                    onClick={() => window.open("mailto:support@myrituality.com", "_self")}
                  >
                    <HelpCircle className="w-4 h-4" />
                    Help Center
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="md:hidden flex flex-col items-center justify-center gap-5">
            <h2 className="text-[26px] leading-normal font-semibold text-primary text-center">
              Center Your Profile Picture
            </h2>
            <p className="md:mb-4 px-3.5 md:px-0 text-[13px] text-[#A1A1A6] text-center">
              Upload an image and adjust its position and zoom to get the perfect profile picture.
            </p>
          </div>
        </div>

        <div className="hidden md:block mb-4 flex items-center justify-between">
          <h2 className="text-[21px] font-semibold text-white text-center flex-1 mt-4">
            Center Your Profile Picture
          </h2>
        </div>

        <p className="md:mb-4 px-3.5 md:px-0 text-[13px] text-[#A1A1A6] text-center hidden md:block">
          Upload an image and adjust its position and zoom to get the perfect profile picture.
        </p>

        <div className="relative mb-4 flex items-center justify-center">
          <div className="h-[350px] w-[350px] overflow-hidden rounded-full bg-black">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={3}
              aspect={1}
              cropShape="round"
              objectFit="cover"
              onCropChange={onCropChange}
              onCropComplete={handleCropComplete}
              onZoomChange={onZoomChange}
              style={{
                containerStyle: {
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#000000",
                },
                mediaStyle: {
                  backgroundColor: "#000000",
                },
              }}
            />
          </div>
        </div>

        <div className="md:mb-6 flex items-center gap-4">
          <input
            type="range"
            min={minZoom}
            max={3}
            step={0.05}
            value={zoom}
            onChange={e => onZoomChange(parseFloat(e.target.value))}
            className="flex-1 h-1 bg-[#38383A] rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-terracotta"
          />
        </div>

        <div className="mb-6 flex flex-col md:flex-row gap-5 md:gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept={THUMBNAIL_LIMITS.ALLOWED_TYPES.join(",")}
            onChange={handleFileChange}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={handleReplaceImage}
            className="flex-1 flex items-center justify-center gap-2 text-sm text-[#0A84FF] hover:text-[#0A84FF]/80 transition-colors uppercase"
          >
            <UploadLinkIcon className="size-4" />
            Upload New Image
          </Button>
          <Button
            onClick={handleApply}
            disabled={isProcessing || !croppedAreaPixels}
            className="flex-1 uppercase"
          >
            {isProcessing ? "Processing..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
